<template>
  <div class="order-admin-zone min-w-0 w-full">
    <div class="order-admin-zone__panel border border-yellow-300 rounded overflow-hidden">
      <!-- En-tête legacy panel-warning -->
      <div
        class="order-admin-zone__heading px-3 py-2 bg-[#fcf8e3] border-b border-yellow-300"
      >
        <span class="font-semibold text-[#8a6d3b]">Admin</span>
      </div>

      <!-- Tableau récap (comme legacy panel-warning) -->
      <table class="w-full text-sm bg-white">
        <tbody>
          <tr class="border-b border-gray-200">
            <td class="px-3 py-2">
              <strong>Quantité totale :</strong> {{ quantityTotal }}
            </td>
            <td class="px-3 py-2">
              <strong>&gt;Poids total (kg)&lt; :</strong>
              {{ formatWeight(poidsTotal) }} kg
            </td>
          </tr>
          <tr class="border-b border-gray-200">
            <td class="px-3 py-2"><strong>Prix total</strong></td>
            <td class="px-3 py-2">
              {{ formatMoney(montantTotal) }}
              <span class="text-gray-600">HT</span>
            </td>
          </tr>
          <template v-if="hasReduction">
            <tr class="border-b border-gray-200">
              <td class="px-3 py-2">
                <strong>Remise <span class="text-gray-600">HT</span> :</strong>
              </td>
              <td class="px-3 py-2">
                {{ formatMoney(reductionAmount) }}
                <span class="text-gray-600">HT</span>
              </td>
            </tr>
            <tr class="border-b border-gray-200">
              <td class="px-3 py-2">
                <strong>Prix HT après remise :</strong>
              </td>
              <td class="px-3 py-2">
                {{ formatMoney(montantApresRemiseDisplay) }}
                <span class="text-gray-600">HT</span>
              </td>
            </tr>
          </template>
          <template v-if="!isEtranger">
            <tr class="border-b border-gray-200">
              <td class="px-3 py-2">
                <strong>TVA ({{ Math.round(tvaRate * 100) }}%) :</strong>
              </td>
              <td class="px-3 py-2">{{ formatMoney(tvaAmount) }}</td>
            </tr>
            <tr class="border-b border-gray-200">
              <td class="px-3 py-2"><strong>Prix total</strong></td>
              <td class="px-3 py-2">
                {{ formatMoney(totalTTC) }}
                <span class="text-gray-600">TTC</span>
              </td>
            </tr>
          </template>
          <tr class="border-b border-gray-200">
            <td class="px-3 py-2"><strong>Frais de port</strong></td>
            <td class="px-3 py-2">
              {{ formatMoney(fraisPortHt) }}
              <span class="text-gray-600">HT</span>
            </td>
          </tr>
          <tr v-if="remiseFraisPortHt > 0" class="border-b border-gray-200">
            <td class="px-3 py-2">
              <strong>Remise Frais de port <span class="text-gray-600">HT</span> :</strong>
            </td>
            <td class="px-3 py-2">
              {{ formatMoney(remiseFraisPortHt) }}
              <span class="text-gray-600">HT</span>
            </td>
          </tr>
          <template v-if="!isEtranger">
            <tr class="border-b border-gray-200">
              <td class="px-3 py-2"><strong>Frais de port</strong></td>
              <td class="px-3 py-2">
                {{ formatMoney(fraisPortTtc) }}
                <span class="text-gray-600">TTC</span>
              </td>
            </tr>
          </template>
          <tr v-if="showFactureFraisPortDownload">
            <td colspan="2" class="px-3 py-3">
              <div class="flex flex-col items-start gap-2">
                <button
                  type="button"
                  :disabled="downloadingFactureFraisPort"
                  class="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                  @click="openFactureFraisPort"
                >
                  <i class="fas fa-download text-gray-600"></i>
                  Télécharger la facture de frais de port
                </button>
                <button
                  v-if="showAvoirDownload"
                  type="button"
                  class="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                  @click="openAvoir"
                >
                  <i class="fas fa-download text-gray-600"></i>
                  Télécharger l'avoir
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import {
  adminEfpGetCreditNoteAuthUrl,
  adminEfpGetShippingInvoiceUrl,
} from "@/api/adminEfp";
import { useNotification } from "@/composables/useNotification";
import { useAuthStore } from "@/stores/auth";
import type { OrderDetailResponse } from "@/types";
import {
  calculateTVARate,
  isEtranger as checkIsEtranger,
} from "@/utils/tvaUtils";

const props = defineProps<{
  orderId: number;
  orderDetail: OrderDetailResponse | null;
}>();

const authStore = useAuthStore();
const { error } = useNotification();
const isAdmin = computed(() => authStore.isImpersonating);

const quantityTotal = computed(() => {
  const lignes = props.orderDetail?.lignes;
  if (!lignes?.length) return 0;
  return lignes
    .filter((l) => l.corbeille === 0)
    .reduce((sum, l) => sum + (l.quantite_total || 0), 0);
});

const poidsTotal = computed(() => props.orderDetail?.poids ?? 0);

const montantTotal = computed(() => props.orderDetail?.montantTotal ?? 0);
const montantCA = computed(() => props.orderDetail?.montantCA ?? 0);
const montantApresRemise = computed(
  () =>
    props.orderDetail?.montantApresRemise ??
    props.orderDetail?.montantTotal ??
    0,
);

const yearBefore2026 = computed(() => {
  const date =
    props.orderDetail?.dateCommande || props.orderDetail?.dateCreation;
  if (!date) return true;
  return new Date(date) < new Date("2026-02-01");
});

const montantApresRemiseDisplay = computed(() =>
  montantCA.value ? montantCA.value : montantApresRemise.value,
);

const hasReduction = computed(() => {
  if (yearBefore2026.value) {
    return montantTotal.value !== montantApresRemise.value;
  }
  const effectiveMontantCA = montantCA.value || montantApresRemise.value;
  return effectiveMontantCA !== montantTotal.value;
});

const reductionAmount = computed(
  () => montantTotal.value - montantApresRemiseDisplay.value,
);

const isEtranger = computed(() => {
  if (!props.orderDetail) return false;
  return checkIsEtranger(props.orderDetail);
});

const tvaRate = computed(() => {
  if (!props.orderDetail) return 0.2;
  return calculateTVARate(
    props.orderDetail.dateCommande || props.orderDetail.dateCreation,
  );
});

const tvaAmount = computed(() => {
  if (isEtranger.value) return 0;
  return montantApresRemiseDisplay.value * tvaRate.value;
});

const totalTTC = computed(() => {
  if (isEtranger.value) return montantApresRemiseDisplay.value;
  return montantApresRemiseDisplay.value * (1 + tvaRate.value);
});

/** Legacy GetFraisPort() → commandeGroupe.frais_port */
const fraisPortHt = computed(() => {
  const fp = props.orderDetail?.commandeGroupe?.frais_port;
  return fp != null ? Number(fp) : 0;
});

const fraisPortSrc = computed(() => {
  const src = props.orderDetail?.commandeGroupe?.frais_port_src;
  return src != null ? Number(src) : 0;
});

/** Legacy $remiseTotalFp = GetRemise(frais_port, "fraisport") */
const remiseFraisPortHt = computed(() => {
  const src = fraisPortSrc.value;
  const fp = fraisPortHt.value;
  if (src > fp) return Math.round((src - fp) * 100) / 100;
  return 0;
});

const fraisPortTtc = computed(() => {
  if (isEtranger.value) return fraisPortHt.value;
  return Math.round(fraisPortHt.value * (1 + tvaRate.value) * 100) / 100;
});

const showFactureFraisPortDownload = computed(() => {
  const d = props.orderDetail;
  if (!d) return false;
  if (d.statut === 5) return false;
  if (d.livraison?.id_livraison === 5) return false;
  return Boolean(
    d.commandeGroupe?.id_commande_groupe_name && d.acheteur?.id_acheteur,
  );
});

const downloadingFactureFraisPort = ref(false);

const openFactureFraisPort = async () => {
  const orderId = props.orderId;
  if (!orderId || downloadingFactureFraisPort.value) return;
  const popup = window.open("about:blank", "_blank");
  downloadingFactureFraisPort.value = true;
  try {
    const url = await adminEfpGetShippingInvoiceUrl(orderId);
    if (url && popup) {
      popup.opener = null;
      popup.location.href = url;
    } else if (url) {
      window.location.href = url;
    }
  } catch (e) {
    popup?.close();
    error("Erreur lors du téléchargement de la facture de frais de port");
    console.error(e);
  } finally {
    downloadingFactureFraisPort.value = false;
  }
};

const avoirUrl = ref("");
const showAvoirDownload = computed(() => Boolean(avoirUrl.value));
let avoirAvailabilityRequest = 0;

const loadAvoirAvailability = async () => {
  const requestId = ++avoirAvailabilityRequest;
  const idAcheteur = props.orderDetail?.acheteur?.id_acheteur;
  const idCommandeGroupe = props.orderDetail?.id_commande_groupe;
  avoirUrl.value = "";
  if (!isAdmin.value || !idAcheteur || !idCommandeGroupe) return;

  try {
    const url = await adminEfpGetCreditNoteAuthUrl(
      idAcheteur,
      idCommandeGroupe,
    );
    if (requestId === avoirAvailabilityRequest) {
      avoirUrl.value = url || "";
    }
  } catch {
    if (requestId === avoirAvailabilityRequest) avoirUrl.value = "";
  }
};

const openAvoir = () => {
  if (!avoirUrl.value) return;
  const popup = window.open(avoirUrl.value, "_blank", "noopener,noreferrer");
  if (!popup) window.location.href = avoirUrl.value;
};

const formatMoney = (value: number): string =>
  `${value.toFixed(2)} €`;

const formatWeight = (value: number): string => value.toFixed(3);

watch(
  () => [
    isAdmin.value,
    props.orderDetail?.acheteur?.id_acheteur,
    props.orderDetail?.id_commande_groupe,
  ] as const,
  loadAvoirAvailability,
  { immediate: true },
);
</script>
